import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  AppBar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import AuthContext from '../../context/authContext';
import CountContext from '../../context/countContext';
import useDeviceTypeRequest from '../../hooks/useDeviceTypeRequest';
import { deviceTypeLabels } from '../../util/deviceInfo';
import { viewRootTextPrimarySx } from '../../util/chartTheme';
import { NumberChip } from '../Bookings/components/BookingIcons';

function statusLabel(status) {
  const s = String(status || '').trim();
  if (s === 'pending') return 'offen';
  if (s === 'approved') return 'freigegeben';
  if (s === 'rejected') return 'abgelehnt';
  return s || '-';
}

function statusChip(status) {
  const s = String(status || '');
  if (s === 'pending') return <Chip size='small' label={statusLabel(s)} color='warning' />;
  if (s === 'approved') return <Chip size='small' label={statusLabel(s)} color='success' />;
  if (s === 'rejected') return <Chip size='small' label={statusLabel(s)} color='default' />;
  return <Chip size='small' label={statusLabel(s)} />;
}

function formatUser(user, fallbackName) {
  if (user && (user.firstName || user.lastName)) {
    return [user.firstName, user.lastName].filter(Boolean).join(' ');
  }
  if (user && user.username) return user.username;
  return fallbackName || '-';
}

function DeviceTypeRequests() {
  const authCtx = useContext(AuthContext);
  const countCtx = useContext(CountContext);
  const {
    getDeviceTypeRequests,
    approveDeviceTypeRequest,
    rejectDeviceTypeRequest,
    getDevices,
    revokeDevice,
  } = useDeviceTypeRequest();

  const [tab, setTab] = useState(0);
  const [devices, setDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [includeRevoked, setIncludeRevoked] = useState(false);
  const [status, setStatus] = useState('pending');
  const [isLoading, setIsLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [busyRequestIds, setBusyRequestIds] = useState(() => new Set());
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectDialogRequest, setRejectDialogRequest] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const busySet = useMemo(() => busyRequestIds, [busyRequestIds]);

  // Number of open (pending) device type requests, kept live via CountContext
  // (registered through the menu item's autoStartCount + counts:invalidate pushes).
  const openRequestCount = countCtx?.counts?.deviceTypeRequests || 0;

  const markBusy = (id, next) => {
    setBusyRequestIds(prev => {
      const copy = new Set(prev);
      if (next) copy.add(id);
      else copy.delete(id);
      return copy;
    });
  };

  const load = async () => {
    setIsLoading(true);
    try {
      const results = await getDeviceTypeRequests(status);
      setRequests(Array.isArray(results) ? results : []);
    } catch (_error) {
      // toast handled in hook
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const loadDevices = async () => {
    setDevicesLoading(true);
    try {
      const results = await getDevices({ includeRevoked });
      setDevices(Array.isArray(results) ? results : []);
    } catch (_error) {
      // toast handled in hook
    } finally {
      setDevicesLoading(false);
    }
  };

  useEffect(() => {
    if (tab === 1) loadDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, includeRevoked]);

  const handleRevokeDevice = async deviceId => {
    markBusy(deviceId, true);
    try {
      await revokeDevice(deviceId);
      toast.success('Gerät widerrufen');
      await loadDevices();
    } catch (_error) {
      // toast handled in hook
    } finally {
      markBusy(deviceId, false);
    }
  };

  const handleApprove = async requestId => {
    markBusy(requestId, true);
    try {
      await approveDeviceTypeRequest(requestId);
      toast.success('Gerätetyp-Anfrage freigegeben');
      await load();
      countCtx?.refreshCount?.('deviceTypeRequests');
    } catch (_error) {
      // toast handled in hook
    } finally {
      markBusy(requestId, false);
    }
  };

  const openRejectDialog = requestDoc => {
    setRejectDialogRequest(requestDoc);
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  const closeRejectDialog = () => {
    setRejectDialogOpen(false);
    setRejectDialogRequest(null);
    setRejectReason('');
  };

  const submitReject = async () => {
    const requestId = rejectDialogRequest?._id;
    if (!requestId) return;
    markBusy(requestId, true);
    try {
      await rejectDeviceTypeRequest(requestId, rejectReason.trim());
      toast.success('Gerätetyp-Anfrage abgelehnt');
      closeRejectDialog();
      await load();
      countCtx?.refreshCount?.('deviceTypeRequests');
    } catch (_error) {
      // toast handled in hook
    } finally {
      markBusy(requestId, false);
    }
  };

  if (!authCtx.checkRight('deviceTypeRequest:approve')) {
    return <Navigate to='/welcome' replace />;
  }

  return (
    <Box sx={{ ...viewRootTextPrimarySx, p: { xs: 2, md: 4 } }}>
      <Typography variant='h5' fontWeight='bold' sx={{ mb: 1 }}>
        Gerätetyp-Anfragen & Geräte
      </Typography>

      <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              Anfragen
              {openRequestCount > 0 && (
                <NumberChip number={openRequestCount} size='small' color='error.main' />
              )}
            </Box>
          }
        />
        <Tab label='Registrierte Geräte' />
      </Tabs>

      {tab === 0 && (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
              mb: 2,
            }}
          >
            <FormControl size='small' sx={{ minWidth: 160, flex: { xs: 1, sm: 'none' } }}>
              <InputLabel>Status</InputLabel>
              <Select value={status} label='Status' onChange={e => setStatus(e.target.value)}>
                <MenuItem value='pending'>offen</MenuItem>
                <MenuItem value='approved'>freigegeben</MenuItem>
                <MenuItem value='rejected'>abgelehnt</MenuItem>
                <MenuItem value=''>alle</MenuItem>
              </Select>
            </FormControl>
            <Button variant='outlined' onClick={load} disabled={isLoading}>
              Aktualisieren
            </Button>
          </Box>

          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={2}>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>Angefragt am</TableCell>
                <TableCell>Nutzer</TableCell>
                <TableCell>Gewünschter Typ</TableCell>
                <TableCell>Gerät</TableCell>
                <TableCell>Gerätesignatur</TableCell>
                <TableCell>Kommentar</TableCell>
                <TableCell align='right'>Aktion</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Typography color='text.secondary'>Keine Einträge.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                requests.map(r => {
                  const id = r._id;
                  const isBusy = busySet.has(id);
                  const createdAt = r.createdAt
                    ? dayjs(r.createdAt).format('DD.MM.YYYY HH:mm')
                    : '-';
                  const deviceLabel = r.deviceInfo?.label || '–';
                  const ua = r.deviceInfo?.userAgent || '';
                  const platform = r.deviceInfo?.platform || '';
                  const deviceDetail = [platform, ua].filter(Boolean).join(' · ');
                  const signature = r.deviceSignature || '';
                  const shortSig = signature ? `${signature.slice(0, 16)}…` : '-';
                  const decisionInfo =
                    r.status === 'rejected' && r.rejectionReason
                      ? `Ablehnung: ${r.rejectionReason}`
                      : r.reviewedByName
                        ? `Geprüft von ${r.reviewedByName}`
                        : '';
                  const commentText = r.reason || '–';

                  return (
                    <TableRow key={id}>
                      <TableCell>{statusChip(r.status)}</TableCell>
                      <TableCell>{createdAt}</TableCell>
                      <TableCell>{formatUser(r.user, r.userName)}</TableCell>
                      <TableCell>
                        <Chip
                          size='small'
                          variant='outlined'
                          label={deviceTypeLabels[r.requestedDeviceType] || r.requestedDeviceType}
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title={deviceDetail || 'Keine Gerätedetails'}>
                          <Box
                            sx={{
                              maxWidth: 200,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            <Typography variant='body2'>{deviceLabel}</Typography>
                            <Typography variant='caption' color='text.secondary'>
                              {deviceDetail || '—'}
                            </Typography>
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={signature || '-'}>
                          <Box
                            component='code'
                            sx={{ fontSize: 12, cursor: signature ? 'help' : 'default' }}
                          >
                            {shortSig}
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        sx={{
                          maxWidth: 240,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                        title={decisionInfo ? `${commentText}\n${decisionInfo}` : commentText}
                      >
                        {decisionInfo ? `${commentText} · ${decisionInfo}` : commentText}
                      </TableCell>
                      <TableCell align='right'>
                        {r.status === 'pending' ? (
                          <Box sx={{ display: 'inline-flex', gap: 1 }}>
                            <Button
                              size='small'
                              variant='contained'
                              onClick={() => handleApprove(id)}
                              disabled={isBusy}
                            >
                              Freigeben
                            </Button>
                            <Button
                              size='small'
                              variant='outlined'
                              color='error'
                              onClick={() => openRejectDialog(r)}
                              disabled={isBusy}
                            >
                              Ablehnen
                            </Button>
                          </Box>
                        ) : (
                          <Typography color='text.secondary'>-</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
          )}
        </>
      )}

      {tab === 1 && (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
              mb: 2,
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  size='small'
                  checked={includeRevoked}
                  onChange={e => setIncludeRevoked(e.target.checked)}
                />
              }
              label='Widerrufene anzeigen'
            />
            <Button variant='outlined' onClick={loadDevices} disabled={devicesLoading}>
              Aktualisieren
            </Button>
          </Box>

          {devicesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={2}>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>Status</TableCell>
                    <TableCell>Nutzer</TableCell>
                    <TableCell>Typ</TableCell>
                    <TableCell>Gerät</TableCell>
                    <TableCell>Gerätesignatur</TableCell>
                    <TableCell>Registriert</TableCell>
                    <TableCell>Zuletzt genutzt</TableCell>
                    <TableCell align='right'>Aktion</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {devices.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8}>
                        <Typography color='text.secondary'>Keine Einträge.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    devices.map(d => {
                      const id = d._id;
                      const isBusy = busySet.has(id);
                      const ua = d.deviceInfo?.userAgent || '';
                      const platform = d.deviceInfo?.platform || '';
                      const deviceDetail = [platform, ua].filter(Boolean).join(' · ');
                      const signature = d.deviceSignature || '';
                      const shortSig = signature ? `${signature.slice(0, 16)}…` : '-';
                      return (
                        <TableRow key={id}>
                          <TableCell>
                            {d.isRevoked ? (
                              <Chip size='small' label='widerrufen' color='default' />
                            ) : (
                              <Chip size='small' label='aktiv' color='success' />
                            )}
                          </TableCell>
                          <TableCell>{formatUser(d.user, d.userName)}</TableCell>
                          <TableCell>
                            <Chip
                              size='small'
                              variant='outlined'
                              label={deviceTypeLabels[d.deviceType] || d.deviceType}
                            />
                          </TableCell>
                          <TableCell>
                            <Tooltip title={deviceDetail || 'Keine Gerätedetails'}>
                              <Box
                                sx={{
                                  maxWidth: 200,
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                <Typography variant='body2'>{d.label || '–'}</Typography>
                                <Typography variant='caption' color='text.secondary'>
                                  {deviceDetail || '—'}
                                </Typography>
                              </Box>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Tooltip title={signature || '-'}>
                              <Box component='code' sx={{ fontSize: 12 }}>
                                {shortSig}
                              </Box>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            {d.registeredAt
                              ? dayjs(d.registeredAt).format('DD.MM.YYYY HH:mm')
                              : '-'}
                          </TableCell>
                          <TableCell>
                            {d.lastUsedAt ? dayjs(d.lastUsedAt).format('DD.MM.YYYY HH:mm') : '-'}
                          </TableCell>
                          <TableCell align='right'>
                            {d.isRevoked ? (
                              <Typography color='text.secondary'>-</Typography>
                            ) : (
                              <Button
                                size='small'
                                variant='outlined'
                                color='error'
                                onClick={() => handleRevokeDevice(id)}
                                disabled={isBusy}
                              >
                                Widerrufen
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      <Dialog open={rejectDialogOpen} onClose={closeRejectDialog} maxWidth='sm' fullWidth>
        <AppBar position='static' color='default' elevation={1}>
          <Toolbar>
            <Typography variant='h6' component='div' sx={{ flexGrow: 1 }}>
              Anfrage ablehnen
            </Typography>
            <IconButton edge='end' color='inherit' onClick={closeRejectDialog}>
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant='body2' color='text.secondary'>
            Der Ablehnungsgrund wird dem Antragsteller beim nächsten Anmelden angezeigt.
          </Typography>
          <TextField
            label='Ablehnungsgrund'
            placeholder='z.B. Gerät konnte nicht verifiziert werden'
            value={rejectReason}
            onChange={e => setRejectReason(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            autoFocus
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button variant='text' onClick={closeRejectDialog}>
              Abbrechen
            </Button>
            <Button variant='contained' color='error' onClick={submitReject}>
              Ablehnen
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
}

export default DeviceTypeRequests;
